/**
 * Titan-X Central API Client
 * ===========================
 * Single source of truth for ALL backend API calls.
 *
 * Rules:
 *  - No component or store may call `fetch()` directly against the API.
 *  - All API calls go through this file.
 *  - Adding a new endpoint = add a function here first.
 *  - Changing an endpoint URL = change it here only, not across 40 files.
 */

// Auth is self-hosted (OCI/PostgreSQL JWT) — no Firebase import needed.
import {
  TaskConfig,
  OpenRouterModel,
  TitanStatus,
  MarketRegime,
  CircuitBreakerStatus,
  TradingDecision,
  AiUsage,
  AiKey,
  BugReport,
  AdminUser,
  WarRoomSettings,
  SystemHealth,
  DeployLog,
  TitanPosition,
  PatternSummary,
  MarketStatus,
  MarketSnapshot,
  PatternUniverse,
  PatternUniverseStats,
  WarRoomAuditListResponse,
  PerformanceSummary,
  ReportingStatsResponse,
  TimeOfDayBucket,
  PatternMetrics,
  SectorMetrics,
  ExecutionQuality,
  BrokerOrder,
  TradingEvaluation,
  TradingLog,
  TitanPattern,
  TranslatePatternRequest,
  AiGuardSettings,
  SignalGuardMode,
  AutonomyLaneConfig,
  AutonomyStrategyPayload,
  AutonomySummaryResponse,
  CryptoAutonomyBriefResponse,
  ForexAutonomyBriefResponse,
  TradingHealth,
  SystemTrajectory,
  ShadowSoakResponse,
} from '../types/titan';
import type { VerticalsSummaryResponse } from '../types/verticals';

export type { VerticalsSummaryResponse } from '../types/verticals';

export type {
  TaskConfig,
  OpenRouterModel,
  TitanStatus,
  MarketRegime,
  CircuitBreakerStatus,
  TradingDecision,
  AiUsage,
  AiKey,
  BugReport,
  AdminUser,
  WarRoomSettings,
  SystemHealth,
  DeployLog,
  TitanPosition,
  PatternSummary,
  MarketStatus,
  MarketSnapshot,
  PatternUniverse,
  PatternUniverseStats,
  WarRoomAuditListResponse,
  PerformanceSummary,
  ReportingStatsResponse,
  TimeOfDayBucket,
  PatternMetrics,
  SectorMetrics,
  ExecutionQuality,
  BrokerOrder,
  TradingEvaluation,
  TradingLog,
  TitanPattern,
  TranslatePatternRequest,
  AiGuardSettings,
  SignalGuardMode,
  AutonomyLaneConfig,
  AutonomyStrategyPayload,
  AutonomySummaryResponse,
  CryptoAutonomyBriefResponse,
  ForexAutonomyBriefResponse,
  TradingHealth,
};

// ─── Base ─────────────────────────────────────────────────────────────────────

/**
 * Production API on Oracle Cloud ARM64 VM behind nginx + Let's Encrypt TLS.
 * CORS allows titan-trading.live and Firebase hosting origins in core/api_server.py.
 */
export const PRODUCTION_API_BASE = 'https://api.titan-trading.live';

/** Hostnames served from Firebase / custom UI (not the API host). */
function isHostedUiOrigin(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h === 'titan-trading.live' ||
    h.endsWith('.titan-trading.live') ||
    h.endsWith('.web.app') ||
    h.endsWith('.firebaseapp.com')
  );
}

/**
 * API origin for REST calls.
 *
 * Override with `REACT_APP_API_URL` for local dev against a specific backend.
 * Production builds use PRODUCTION_API_BASE (api.titan-trading.live).
 */
function resolveApiBase(): string {
  const env = process.env.REACT_APP_API_URL;
  if (typeof env === 'string' && env.trim()) {
    return env.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    if (isHostedUiOrigin(window.location.hostname)) {
      return PRODUCTION_API_BASE;
    }
  }
  return PRODUCTION_API_BASE;
}

export const API_BASE = resolveApiBase();

/**
 * WebSocket base URL. Derives from API_BASE, converting https→wss / http→ws.
 * Override with REACT_APP_WS_URL for local dev.
 */
export function getWebSocketBase(): string {
  const env = process.env.REACT_APP_WS_URL;
  if (typeof env === 'string' && env.trim()) {
    return env.trim().replace(/\/$/, '');
  }
  return API_BASE.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
}

/** Get the locally-issued JWT (OCI/PostgreSQL auth) for every request. */
async function getToken(): Promise<string> {
  const token = localStorage.getItem('titan_token');
  if (!token) throw new Error('Not authenticated');
  return token;
}

/** Standard auth headers. */
async function authHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Auth headers without Content-Type (for GET requests). */
async function getHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Core fetch wrapper.
 * - Automatically attaches auth headers.
 * - Throws a typed ApiError on non-2xx responses.
 * - Retries once on 401 after refreshing the token.
 * - Retries 429/503 with backoff (GCP edge often returns these without CORS; backing off reduces churn).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly url: string,
  ) {
    super(`API ${status} at ${url}: ${detail}`);
    this.name = 'ApiError';
  }
}

/** Briefly pause all outbound API calls after a rate-limit response (reduces stampedes). */
let globalApiCooldownUntil = 0;

const MAX_RATE_LIMIT_RETRIES = 3;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  authRetried = false,
  rateLimitAttempt = 0,
): Promise<T> {
  const coolMs = globalApiCooldownUntil - Date.now();
  if (coolMs > 0 && coolMs < 120000) {
    await sleep(coolMs);
  }

  let res: Response;
  try {
    const headers = await (method === 'GET' ? getHeaders() : authHeaders());
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    const originHint =
      API_BASE ||
      (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'API');
    throw new ApiError(
      503,
      `Cannot reach API (${originHint}): ${msg}`,
      path,
    );
  }

  // Retry once on 401 (token may have expired mid-session)
  if (res.status === 401 && !authRetried) {
    return request<T>(method, path, body, signal, true, rateLimitAttempt);
  }

  if (
    (res.status === 429 || res.status === 503 || res.status === 502 || res.status === 504) &&
    rateLimitAttempt < MAX_RATE_LIMIT_RETRIES
  ) {
    try {
      await res.text();
    } catch {
      /* ignore */
    }
    const ra = res.headers.get('Retry-After');
    let waitMs: number;
    if (ra) {
      const sec = parseInt(ra, 10);
      waitMs = Number.isFinite(sec) ? sec * 1000 : 2500 * (rateLimitAttempt + 1);
    } else {
      waitMs = Math.min(2000 * 2 ** rateLimitAttempt, 45000);
    }
    waitMs = Math.min(Math.max(waitMs, 800), 60000);
    globalApiCooldownUntil = Date.now() + waitMs;
    await sleep(waitMs);
    return request<T>(method, path, body, signal, authRetried, rateLimitAttempt + 1);
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      const raw = err.detail ?? err.message;
      if (typeof raw === 'string') {
        detail = raw;
      } else if (raw && typeof raw === 'object' && typeof raw.message === 'string') {
        detail = raw.message;
      } else if (raw != null) {
        detail = JSON.stringify(raw);
      } else {
        detail = JSON.stringify(err);
      }
    } catch {
      try {
        const t = await res.text();
        if (t) detail = t.length > 200 ? `${t.slice(0, 200)}…` : t;
      } catch {
        // ignore parse errors
      }
    }
    if (res.status === 502 || res.status === 504) {
      detail = `${detail} — Gateway error: upstream may be overloaded, rolling out, or timing out. Missing CORS headers on 502/504 HTML from a proxy often produce a misleading browser CORS message.`;
    }
    throw new ApiError(res.status, detail, path);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Unauthenticated JSON POST (e.g. public registration). */
async function requestPublic<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new ApiError(503, `Cannot reach API (${API_BASE}): ${msg}`, path);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const errJson = await res.json();
      detail = errJson.detail || errJson.message || JSON.stringify(errJson);
    } catch {
      try {
        const t = await res.text();
        if (t) detail = t.length > 200 ? `${t.slice(0, 200)}…` : t;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, detail, path);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const publicApi = {
  register: (body: {
    email: string;
    password: string;
    displayName: string;
    invitation_code?: string;
  }) =>
    requestPublic<{ status?: string; uid?: string }>('POST', '/public/register', body),
};

const get  = <T>(path: string, signal?: AbortSignal)              => request<T>('GET',    path, undefined, signal);
const post = <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>('POST',   path, body, signal);
const put  = <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>('PUT',    path, body, signal);
const patch= <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>('PATCH',  path, body, signal);
const del  = <T>(path: string, signal?: AbortSignal)              => request<T>('DELETE', path, undefined, signal);


// ─── Titan Controller ─────────────────────────────────────────────────────────

export const titanApi = {
  // Compat: /api/titan/health is no longer present in backend; use status payload.
  health:          () => get<TitanStatus>('/api/titan/status'),
  status:          () => get<TitanStatus>('/api/titan/status'),
  warRoomDashboard: () =>
    get<{ strategy: any; learnings: any[]; principles: any[] }>('/api/titan/war-room/dashboard'),
  equitiesMode:    () =>
    get<{
      equities_only: boolean;
      execution_focus_ui: boolean;
      rt_pattern_eval_enabled: boolean;
      max_live_patterns: number;
      api_worker_mode: string;
      controller_allowed_on_process: boolean;
      read_only_api_process: boolean;
    }>('/api/titan/equities-mode'),
  microcapWatchlist: () =>
    get<{ success: boolean; config: Record<string, unknown> }>('/api/titan/microcap-watchlist'),
  saveMicrocapWatchlist: (body: Record<string, unknown>) =>
    put<{ success: boolean; config: Record<string, unknown>; message?: string }>(
      '/api/titan/microcap-watchlist',
      body,
    ),
  automationActivity: (limit = 50) =>
    get<{ events: Array<Record<string, unknown>>; total: number }>(`/api/titan/automation/activity?limit=${limit}`),
  dataAvailability: () =>
    get<{ success: boolean; data: Record<string, unknown> }>('/api/titan/monitoring/data-availability'),
  dataAvailabilityEvents: (limit = 100) =>
    get<{ success: boolean; events: Array<Record<string, unknown>>; count: number }>(
      `/api/titan/monitoring/data-availability/events?limit=${limit}`,
    ),
  tradingReadiness: () =>
    get<{ success: boolean; data: Record<string, unknown> }>('/api/titan/health/trading-readiness'),
  whyNoTrades: () => get<TradingHealth>('/api/titan/why-no-trades'),
  start:           () => post<{ success: boolean }>('/api/titan/start'),
  stop:            () => post<{ success: boolean }>('/api/titan/stop'),
  pause:           () => post<{ success: boolean }>('/api/titan/pause'),
  resume:          () => post<{ success: boolean }>('/api/titan/resume'),
  reloadPatterns:  () => post<{ success: boolean }>('/api/titan/reload-patterns'),
  // Compat: /api/titan/market/regime was renamed; use dedicated regime endpoint.
  marketRegime:    () => get<MarketRegime>('/api/titan/regime/status'),
  marketCalendar:  () => get<MarketStatus>('/api/titan/market/calendar'),
  marketSnapshot:  (exchange = 'US') => get<MarketSnapshot>(`/api/titan/market/snapshot?exchange=${exchange}`),
  symbolData:      (symbol: string) => get<any>(`/api/titan/symbol-data/${encodeURIComponent(symbol)}`),
  // Compat: /api/titan/circuit-breaker is represented inside /status.
  circuitBreaker:  () =>
    get<TitanStatus>('/api/titan/status').then((s: any) => s?.circuit_breaker ?? {}),
  killSwitch:      (closePositions = false) => post<{ success: boolean }>('/api/titan/kill-switch', { close_positions: closePositions }),
  resetKillSwitch: () => post<{ success: boolean }>('/api/titan/kill-switch/reset'),
  risk:            () => get<any>('/api/titan/risk'),
  reconReport:     () => get<any>('/api/titan/reconciliation/last-report'),
  simulateSignalGuard: (symbol: string) => post<any>('/api/titan/simulate/signal-guard', { symbol }),
  assetSessions:   () => get<any>('/api/titan/asset-sessions'),
  resumeAssetSession: (assetClass: string) => post<any>(`/api/titan/asset-sessions/${assetClass}/resume`, {}),
  warRoomAuditList: (params: { limit?: number; start_date?: string; end_date?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.start_date) q.set('start_date', params.start_date);
    if (params.end_date) q.set('end_date', params.end_date);
    const qs = q.toString();
    return get<WarRoomAuditListResponse>(`/api/titan/war-room/audit${qs ? `?${qs}` : ''}`);
  },
  warRoomAuditDetail: (auditDate: string, includeRawResponse = false) =>
    get<Record<string, unknown>>(
      `/api/titan/war-room/audit/${encodeURIComponent(auditDate)}?include_raw_response=${includeRawResponse}`,
    ),
  discoveryPatternSuggestions: (minTrades = 5) =>
    get<{
      scale_up_candidates: Array<Record<string, unknown>>;
      review_or_quarantine: Array<Record<string, unknown>>;
      total_patterns_tracked: number;
      note?: string;
    }>(`/api/titan/discovery/pattern-suggestions?min_trades=${minTrades}`),
  startReplay: (symbol: string, date: string, interval = '1m') => 
    post<any>('/api/titan/replay/start', { symbol, date, interval }),
  replayStatus: (id: string) => get<any>(`/api/titan/replay/status/${id}`),
  premarketWatchlist: () => get<any>('/api/titan/premarket/watchlist'),
  autopsy: (days: number) => get<any>(`/api/titan/performance/autopsy?days=${days}`),
  forwardReturns: (days: number, samplePerReason: number) => post<any>(`/api/titan/performance/autopsy/forward-returns?days=${days}&sample_per_reason=${samplePerReason}`),
  
  // Broker / Positions (moved from brokerApi)
  positions:    () => get<{ positions: TitanPosition[] }>('/api/titan/broker/positions'),
  orders:       (params: { limit?: number; status_filter?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.status_filter) q.set('status_filter', params.status_filter);
    return get<{ orders: BrokerOrder[] }>(`/api/titan/broker/orders?${q}`);
  },
  liquidate:    (symbol: string) =>
    post<{ success: boolean; filled?: boolean; message?: string }>(
      `/api/titan/broker/positions/${symbol}/liquidate`),
  liquidateAll: () =>
    post<{ success: boolean; pending_market_open?: boolean; message?: string; still_open?: string[] }>(
      '/api/titan/broker/liquidate-all'),
  reconcile:    () => post<Record<string, unknown>>('/api/titan/broker/reconcile'),
  reconcileFull: (params?: { close_ghosts?: boolean; import_external?: boolean; relabel_matched?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.close_ghosts === false) q.set('close_ghosts', 'false');
    if (params?.import_external === false) q.set('import_external', 'false');
    if (params?.relabel_matched === false) q.set('relabel_matched', 'false');
    const qs = q.toString();
    return post<Record<string, unknown>>(`/api/titan/broker/reconcile-full${qs ? `?${qs}` : ''}`);
  },
  protectAll: (slPct: number, tpPct: number) => post<any>(`/api/titan/broker/positions/protect-all?stop_loss_pct=${slPct}&take_profit_pct=${tpPct}`),
  updateExitPrice: (symbol: string, params: { stop_loss_price?: string, take_profit_price?: string }) => {
    const q = new URLSearchParams(params).toString();
    return put<any>(`/api/titan/broker/positions/${symbol}/exit-prices?${q}`);
  },
  exitProtection: () => get<Record<string, unknown>>('/api/titan/positions/exit-protection'),
  runWarRoomStrategy: (reason = 'manual_request', force = false) =>
    post<Record<string, unknown>>(
      `/api/titan/autonomy/strategy/run?reason=${encodeURIComponent(reason)}&force=${force}`,
    ),
};

// ─── Decisions & Logs ─────────────────────────────────────────────────────────

export const botInsightApi = {
  summary: (hours = 24) =>
    get<{
      hours: number;
      bot_count: number;
      platform?: {
        controller_present?: boolean;
        is_running?: boolean;
        scan_safe?: boolean;
        consecutive_stale?: number;
        last_quote_age_seconds?: number;
        data_feed_mode?: string;
        pattern_count?: number;
      };
      bots: Array<{
        pattern_id: string;
        pattern_name: string;
        is_active: boolean;
        trace_count: number;
        blocked_count: number;
        scanned_count: number;
        eval_count: number;
        match_count: number;
        signal_count: number;
        last_trace: string | null;
        last_trace_summary: Record<string, unknown> | null;
        latest_block_reason: string | null;
        latest_universe_size: number;
        match_rate: number;
        block_rate: number;
      }>;
    }>(`/api/titan/decision-logs/bots?hours=${hours}`),
  detail: (
    patternId: string,
    opts: { hours?: number; eval_limit?: number; trace_limit?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.hours != null) q.set('hours', String(opts.hours));
    if (opts.eval_limit != null) q.set('eval_limit', String(opts.eval_limit));
    if (opts.trace_limit != null) q.set('trace_limit', String(opts.trace_limit));
    return get<Record<string, unknown>>(
      `/api/titan/decision-logs/bots/${patternId}?${q}`,
    );
  },
};

export const decisionsApi = {
  list: (params: { limit?: number; page?: number; symbol?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null).map(([k, v]) => [k, String(v)]));
    return get<{ decisions: TradingDecision[]; total: number }>(`/api/titan/decisions?${q}`);
  },
  evaluations: (params: { limit?: number; days?: number } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null).map(([k, v]) => [k, String(v)]));
    return get<{ evaluations: TradingEvaluation[] }>(`/api/titan/decision-logs/evaluations?${q}`);
  },
  logs: (params: { limit?: number; days?: number; event_type?: string; pattern_id?: string; vertical?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null).map(([k, v]) => [k, String(v)]));
    return get<{ logs: TradingLog[] }>(`/api/titan/decision-logs?${q}`);
  },
  trades: (params: { days?: number; limit?: number; asset_class?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null).map(([k, v]) => [k, String(v)]));
    return get<{ trades: unknown[] }>(`/api/titan/decision-logs/trades?${q}`);
  },
};

// ─── Command Center ─────────────────────────────────────────────────────────
// One unified snapshot composing all 6 telemetry quadrants (see
// api/titan/command_center_routes.py). Each quadrant is heterogeneous (it wraps an
// existing endpoint's payload) and may be an {error} stub if that subsystem is down.
export type CommandCenterQuadrant = Record<string, unknown> & { error?: string; quadrant?: string };

export interface CommandCenterSnapshot {
  ts: string;
  quadrants: {
    decision: CommandCenterQuadrant;
    catalysts: CommandCenterQuadrant;
    data_health: CommandCenterQuadrant;
    financials: CommandCenterQuadrant;
    forecasting: { rolling_performance: CommandCenterQuadrant; catalyst_effectiveness: CommandCenterQuadrant };
    stability: CommandCenterQuadrant;
  };
}

export const commandCenterApi = {
  snapshot: (params: { financials_days?: number; catalyst_max_age_minutes?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
    );
    const qs = q.toString();
    return get<CommandCenterSnapshot>(`/api/command-center/snapshot${qs ? `?${qs}` : ''}`, signal);
  },
};
// ─── Patterns ─────────────────────────────────────────────────────────────────

export const patternsApi = {
  list:        () => get<{ patterns: PatternSummary[] }>('/api/titan/patterns'),
  get:         (id: string) => get<TitanPattern>(`/api/titan/patterns/${id}`),
  create:      (body: unknown) => post<TitanPattern>('/api/titan/patterns', body),
  update:      (id: string, body: unknown) => patch<TitanPattern>(`/api/titan/patterns/${id}`, body),
  delete:      (id: string) => del<{ success: boolean }>(`/api/titan/patterns/${id}`),
  activate:    (id: string) => post<{ success: boolean }>(`/api/titan/patterns/${id}/activate`),
  deactivate:  (id: string) => post<{ success: boolean }>(`/api/titan/patterns/${id}/deactivate`),
  translate:   (body: TranslatePatternRequest) =>
    post<TitanPattern>('/api/titan/patterns/translate', body),
  templates:   () => get<any[]>('/api/titan/patterns/templates'),
  template:    (id: string) => get<any>(`/api/titan/patterns/templates/${id}`),
  templatePreview: (id: string) => get<any>(`/api/titan/patterns/templates/${id}`),
  fromTemplate: (templateId: string, nameOverride?: string) => 
    post<{ pattern_id: string; pattern_name: string }>('/api/titan/patterns/from-template', { template_id: templateId, name_override: nameOverride }),
  universe:    (id: string) => get<PatternUniverse>(`/api/titan/patterns/${id}/universe`),
  universeStats:(id: string) => get<PatternUniverseStats>(`/api/titan/patterns/${id}/universe/stats`),
  universeRefresh: (id: string) => post<PatternUniverse>(`/api/titan/patterns/${id}/universe/refresh`),
};

// ─── Session ──────────────────────────────────────────────────────────────────

export const sessionApi = {
  // Aliases for legacy name compatibility
  summaryHistory: (limit = 30) => get<unknown>(`/api/titan/session-summary/history?limit=${limit}`),
  summaryByDate:  (date: string) => get<unknown>(`/api/titan/session-summary/${date}`),
  generateSummary:(date?: string) => post<unknown>('/api/titan/session-summary/generate', date ? { date } : undefined),
  // Short-form aliases used by SessionSummary.tsx
  history: (limit = 30) => get<unknown>(`/api/titan/session-summary/history?limit=${limit}`),
  get:     (date: string) => get<unknown>(`/api/titan/session-summary/${date}`),
  generate:(date: string) => post<unknown>('/api/titan/session-summary/generate', { date }),
};

// ─── AI Task Routing ──────────────────────────────────────────────────────────

export const aiTasksApi = {
  listTasks:    () =>
    get<{ success: boolean; tasks: Record<string, TaskConfig> }>('/api/intelligence/ai/tasks'),
  // Aliases used by AIModelSettings
  getTasks:     () =>
    get<{ success: boolean; tasks: Record<string, TaskConfig> }>('/api/intelligence/ai/tasks'),
  getModels:    () =>
    get<{ synced?: boolean; synced_at?: string; model_count?: number; free_count?: number; premium_count?: number; cached?: boolean; openrouter: OpenRouterModel[]; models?: OpenRouterModel[] }>(
      '/api/intelligence/ai/models'
    ),
  listModels:   () =>
    get<{ synced?: boolean; cached?: boolean; openrouter: OpenRouterModel[]; models?: OpenRouterModel[] }>(
      '/api/intelligence/ai/models'
    ),
  syncModels:   () => post<{ success: boolean }>('/api/intelligence/ai/models/sync'),
  batchUpdate:  (updates: Record<string, Partial<TaskConfig>>) =>
    put<{ success: boolean; updated_count: number }>('/api/intelligence/ai/tasks/batch/update', updates),
  updateTask:   (taskId: string, updates: Partial<TaskConfig>) =>
    put<{ success: boolean }>(`/api/intelligence/ai/tasks/${taskId}`, updates),
  resetTask:    (taskId: string) =>
    post<{ success: boolean }>(`/api/intelligence/ai/tasks/${taskId}/reset`),
  resetAll:     () => post<{ success: boolean }>('/api/intelligence/ai/tasks/reset-all'),
  listKeys:     () => get<{ keys: AiKey[] }>('/api/intelligence/ai/keys'),
  setKey:       (providerId: string, body: { api_key: string }) =>
    put<{ success: boolean }>(`/api/intelligence/ai/keys/${providerId}`, body),
  updateKey:    (providerId: string, body: { api_key: string }) =>
    put<{ success: boolean }>(`/api/intelligence/ai/keys/${providerId}`, body),
  deleteKey:    (providerId: string) =>
    del<{ success: boolean }>(`/api/intelligence/ai/keys/${providerId}`),
  updateProvider:(providerId: string, body: unknown) =>
    put<{ success: boolean }>(`/api/intelligence/ai/providers/${providerId}`, body),
  usage:        () => get<AiUsage>('/api/intelligence/ai/usage'),
  calls:        (limit = 100, service?: string) => {
    const q = service ? `?limit=${limit}&service=${service}` : `?limit=${limit}`;
    return get<{ calls: unknown[] }>(`/api/intelligence/ai/calls${q}`);
  },
};

// ─── Intelligence / Chat / News ───────────────────────────────────────────────

export const intelligenceApi = {
  chat:             (body: { messages: unknown[]; context?: string }) =>
    post<{ response: string }>('/api/intelligence/chat', body),
  news:             () => get<unknown>('/api/intelligence/news'),
  pulse:            () => get<unknown>('/api/intelligence/pulse'),
  masterControl:    () => get<unknown>('/api/intelligence/master-control/telemetry'),
  masterControlTrends: () => get<unknown>('/api/intelligence/master-control/trends'),
  runCatalystLifecycle: () =>
    post<{ success: boolean; retired_pending: number; deactivated_patterns: number; reason: string }>(
      '/api/intelligence/catalyst-autonomy/lifecycle/run',
      {}
    ),
  financialSummary: (days = 30) => get<unknown>(`/api/intelligence/financial-summary?days=${days}`),
  personaStatus:    () => get<unknown>('/api/intelligence/persona/status'),
};

/** Per-asset-class verticals (stocks / crypto / forex / futures). See `docs/VERTICALS_SPRINT_ROADMAP.md`. */
export const verticalsApi = {
  summary: () => get<VerticalsSummaryResponse>('/api/titan/verticals/summary'),
  pnl: () =>
    get<{
      schema_version: number;
      user_id?: string;
      replica_has_controller?: boolean;
      // "alpaca" when the stocks vertical's realized-today is broker-sourced (ET market
      // day) rather than the UTC position-manager calc. See docs/UNIFIED_REPORTING.md.
      stocks_source?: string;
      verticals: Record<string, { realized_today: number; unrealized: number; open_count: number }>;
      rollup: { realized_today: number; unrealized: number };
    }>('/api/titan/verticals/pnl'),
  getConfig: (verticalId: string) =>
    get<{ vertical: string; config: Record<string, unknown> }>(`/api/titan/verticals/${encodeURIComponent(verticalId)}/config`),
  patchConfig: (verticalId: string, patchPayload: Record<string, unknown>) =>
    patch<{ vertical: string; config: Record<string, unknown> }>(
      `/api/titan/verticals/${encodeURIComponent(verticalId)}/config`,
      { patch: patchPayload },
    ),
  getGoals: (verticalId: string) =>
    get<{ vertical: string; goals: Record<string, unknown> }>(`/api/titan/verticals/${encodeURIComponent(verticalId)}/goals`),
  patchGoals: (verticalId: string, patchPayload: Record<string, unknown>) =>
    patch<{ vertical: string; goals: Record<string, unknown> }>(
      `/api/titan/verticals/${encodeURIComponent(verticalId)}/goals`,
      { patch: patchPayload },
    ),
  runWarRoom: (verticalId: string) =>
    post<{ status: string; vertical?: string; strategy?: unknown; message?: string }>(
      `/api/titan/verticals/${encodeURIComponent(verticalId)}/war-room/run`,
      {},
    ),
  diagnoseWarRoom: (verticalId: string) =>
    get<{ healthy: boolean; issues: string[]; uid: string; vertical: string; [key: string]: unknown }>(
      `/api/titan/verticals/${encodeURIComponent(verticalId)}/war-room/diagnose`,
    ),
};

// ─── Settings / Integrations ──────────────────────────────────────────────────

export const settingsApi = {
  getConfigs: async () => {
    const allSettings = await get<Record<string, unknown>>('/api/settings/all').catch(() => ({} as Record<string, unknown>));
    const userSchedule = await get<Record<string, unknown>>('/api/settings/user/schedule').catch(() => null);
    return {
      status: 'success',
      configs: {
        ...allSettings,
        trading_schedule: userSchedule || allSettings?.trading_schedule || {}
      }
    };
  },
  saveConfigs: async (body: Record<string, unknown>) => {
    if (body.trading_schedule) {
      return patch<{ status: string; message?: string }>('/api/settings/user/schedule', body.trading_schedule);
    }
    return post<{ status: string; message?: string }>('/system/configs', body);
  },
  warRoom:    () => get<{ status: string; settings: WarRoomSettings }>('/api/settings/war-room'),
  saveWarRoom:(settings: WarRoomSettings) => post<{ status: string; message?: string }>('/api/settings/war-room', settings),
  simulate:   () => post<unknown>('/api/simulate/war-room'),
  testWarRoomEmail: () => post<{ status: string; message: string; sent_to?: string[] }>('/api/settings/war-room/test-email', {}),
  getSmtp:    () => get<any>('/api/settings/smtp'),
  saveSmtp:   (body: any) => post<{ status: string; message: string }>('/api/settings/smtp', body),
  aiGuard:    () => get<{ status: string; settings: AiGuardSettings }>('/api/settings/ai-guard'),
  saveAiGuard:(settings: AiGuardSettings) => post<{ status: string; message?: string }>('/api/settings/ai-guard', settings),
  brokerStatus: () => get<any>('/api/settings/broker-status'),
  userRisk: () => get<any>('/api/settings/user/risk'),
  updateRisk: (params: any) => patch<any>('/api/settings/risk', params),
};

/** Independent crypto / forex fleets (outside Daily War Room). See `/autonomy` UI. */
export const autonomyApi = {
  getSummary: () => get<AutonomySummaryResponse>('/api/autonomy/summary'),
  generateCryptoBrief: (body?: { notes?: string }) =>
    post<CryptoAutonomyBriefResponse>('/api/autonomy/crypto/brief', body ?? {}),
  generateForexBrief: (body?: { notes?: string }) =>
    post<ForexAutonomyBriefResponse>('/api/autonomy/forex/brief', body ?? {}),
  getCryptoConfig: () => get<{ config: AutonomyLaneConfig }>('/api/autonomy/crypto/config'),
  saveCryptoConfig: (config: Partial<AutonomyLaneConfig>) =>
    put<{ status: string }>('/api/autonomy/crypto/config', config),
  getCryptoStrategy: () => get<{ strategy: AutonomyStrategyPayload | null }>('/api/autonomy/crypto/strategy'),
  saveCryptoStrategy: (body: AutonomyStrategyPayload) =>
    put<{ status: string }>('/api/autonomy/crypto/strategy', body),
  deployCrypto: (body?: {
    bot_strategies?: Record<string, unknown>[];
    asset_allocation?: Record<string, number>;
  }) => post<{ status: string; patterns_saved: number }>('/api/autonomy/crypto/deploy', body ?? {}),

  getForexConfig: () => get<{ config: AutonomyLaneConfig }>('/api/autonomy/forex/config'),
  saveForexConfig: (config: Partial<AutonomyLaneConfig>) =>
    put<{ status: string }>('/api/autonomy/forex/config', config),
  getForexStrategy: () => get<{ strategy: AutonomyStrategyPayload | null }>('/api/autonomy/forex/strategy'),
  saveForexStrategy: (body: AutonomyStrategyPayload) =>
    put<{ status: string }>('/api/autonomy/forex/strategy', body),
  deployForex: (body?: {
    bot_strategies?: Record<string, unknown>[];
    asset_allocation?: Record<string, number>;
  }) => post<{ status: string; patterns_saved: number }>('/api/autonomy/forex/deploy', body ?? {}),
};

export const credentialsApi = {
  saveTradier: (token: string, accountId: string, isPaper: boolean) =>
    post<any>('/api/settings/credentials/tradier', { bearer_token: token, account_id: accountId, is_paper: isPaper }),
  saveEodhd: (apiKey: string) =>
    post<any>('/api/settings/data-provider', { provider: 'eodhd', api_key: apiKey }),
  saveOpenRouter: (apiKey: string) =>
    put<any>('/api/intelligence/ai/keys/openrouter', { api_key: apiKey }),
  saveAlpaca: (apiKey: string, apiSecret: string, isPaper: boolean) => 
    post<any>('/api/settings/credentials/alpaca', { api_key: apiKey, api_secret: apiSecret, is_paper: isPaper }),
  saveOanda: (accountId: string, accessToken: string, environment: 'practice' | 'live') =>
    post<any>('/api/settings/credentials/oanda', { account_id: accountId, access_token: accessToken, environment }),
  validateOanda: () => post<any>('/api/settings/credentials/oanda/validate', {}),
  saveCoinbase: (keyJson: string) =>
    post<any>('/api/settings/credentials/coinbase', {
      key_json: keyJson,
    }),
  validateCoinbase: () => post<any>('/api/settings/credentials/coinbase/validate', {}),
};

// ─── Analytics ────────────────────────────────────────────────────────────────

/** Unified reporting (Firestore exits, ET market days) — preferred for P&L rollups. */
export const reportingApi = {
  stats: () => get<ReportingStatsResponse>('/api/reporting/stats'),
  systemTrajectory: (days = 90, horizon = 5) =>
    get<SystemTrajectory>(`/api/reporting/system-trajectory?days=${days}&horizon=${horizon}`),
  shadowSoak: (days = 7) =>
    get<ShadowSoakResponse>(`/api/reporting/shadow-soak?days=${days}`),
  summary: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<PerformanceSummary>(`/api/reporting/summary?${params}`);
  },
  byTimeOfDay: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<{ buckets: TimeOfDayBucket[] }>(`/api/reporting/by-time-of-day?${params}`);
  },
  byPattern: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<{ patterns: PatternMetrics[] }>(`/api/reporting/by-pattern?${params}`);
  },
  bySession: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<{ sessions: Array<{ session: string; metrics: Record<string, number> }> }>(
      `/api/reporting/by-session?${params}`,
    );
  },
  executionQuality: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<ExecutionQuality>(`/api/reporting/execution-quality?${params}`);
  },
  equityCurve: (days = 90) =>
    get<{ points: Array<{ timestamp: string; cumulative_pnl: number; drawdown_pct?: number }> }>(
      `/api/reporting/equity-curve?days=${days}`,
    ),
  dailyHistory: (days = 90) =>
    get<{ days: Array<{ date: string; total_pnl: number; total_trades: number; win_rate: number }> }>(
      `/api/reporting/daily-history?days=${days}`,
    ),
  trades: (params: { days?: number; symbol?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.days) q.set('days', String(params.days));
    if (params.symbol) q.set('symbol', params.symbol);
    if (params.limit) q.set('limit', String(params.limit));
    return get<{ trades: Array<Record<string, unknown>>; count: number }>(`/api/reporting/trades?${q}`);
  },
  drawdown: (days = 90) => get<Record<string, unknown>>(`/api/reporting/drawdown?days=${days}`),
  exitLogAudit: (days = 365) =>
    get<Record<string, unknown>>(`/api/reporting/exit-log-audit?days=${days}`),
  persistHistory: (days = 365) =>
    post<{ success: boolean; logs_queued: number }>(`/api/reporting/persist-history?days=${days}`, {}),
  scrubLegacyExits: (params: { days?: number; action?: 'tag' | 'delete'; dry_run?: boolean } = {}) => {
    const q = new URLSearchParams({ days: String(params.days ?? 365), action: params.action ?? 'tag' });
    q.set('dry_run', String(params.dry_run !== false));
    return post<Record<string, unknown>>(`/api/reporting/scrub-legacy-exits?${q}`, {});
  },
};

export const analyticsApi = {
  performanceSummary:  (days = 30, assetClass?: string) => {
    const q = assetClass ? `?days=${days}&asset_class=${assetClass}` : `?days=${days}`;
    return get<PerformanceSummary>(`/api/reporting/summary${q}`);
  },
  byTimeOfDay: (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<{ buckets: TimeOfDayBucket[] }>(`/api/reporting/by-time-of-day?${params}`);
  },
  byPattern:   (days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<{ patterns: PatternMetrics[] }>(`/api/reporting/by-pattern?${params}`);
  },
  bySector:    (days = 30, assetClass?: string) => {
    const q = assetClass ? `?days=${days}&asset_class=${assetClass}` : `?days=${days}`;
    return get<{ sectors: SectorMetrics[] }>(`/api/analytics/performance/by-sector${q}`);
  },
  executionQuality:(days = 30, assetClass?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (assetClass) params.set('asset_class', assetClass);
    return get<ExecutionQuality>(`/api/reporting/execution-quality?${params}`);
  },
};

// ─── Admin ────────────────────────────────────────────────────────────────────

export const adminApi = {
  users:         () => get<{ users: AdminUser[] }>('/api/admin/users'),
  betaVersions:  () => get<any>('/api/admin/beta/versions'),
  betaStatus:    () => get<any>('/api/admin/beta/status'),
  betaUsers:     () => get<any>('/api/admin/beta/users'),
  updateBeta:    (body: unknown) => post<any>('/api/admin/beta/update', body),
  promoteFeature: (id: string) => post<any>(`/api/admin/beta/promote/${id}`),
  demoteFeature:  (id: string) => post<any>(`/api/admin/beta/demote/${id}`),
  grantBeta:      (uid: string) => post<any>(`/api/admin/beta/users/${uid}`),
  revokeBeta:     (uid: string) => del<any>(`/api/admin/beta/users/${uid}`),
  claimAdmin:     () => post<{ success: boolean }>('/api/admin/claim'),
  userAction:    (uid: string, action: string) =>
    post<{ success: boolean }>(`/api/admin/users/${uid}/${action}`),
  updateRole:    (uid: string, role: string) =>
    post<{ success: boolean }>('/users/role', { uid, role }),
  createUser:    (body: unknown) => post<{ success: boolean }>('/users', body),
  createInvitation: (body: {
    email: string;
    role?: 'Admin' | 'Trader' | 'Analyst' | 'Viewer';
    expires_in_hours?: number;
    note?: string;
  }) => post<{
    status: string;
    invitation_id: string;
    email: string;
    code: string;
    role: string;
    expires_at: string;
    email_sent: boolean;
  }>('/users/invitations', body),
  deleteUser:    (uid: string) => del<{ success: boolean }>(`/users/${uid}`),
  impersonate:   (uid: string) =>
    post<{ token?: string; custom_token?: string }>(`/api/admin/users/${uid}/impersonate`),
  bugs:          (params: { limit?: number; status?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null).map(([k, v]) => [k, String(v)]));
    return get<{ reports: BugReport[] }>(`/api/admin/bugs?${q}`);
  },
  getBug:        (id: string) => get<BugReport>(`/api/admin/bugs/${id}`),
  resolveBug:    (id: string, body: unknown) =>
    patch<{ success: boolean }>(`/api/admin/bugs/${id}`, body),
  deleteBug:     (id: string) => del<{ success: boolean }>(`/api/admin/bugs/${id}`),
  analyzeBug:    (id: string) => post<{ success: boolean }>(`/api/admin/bugs/${id}/analyze`),
  deploys:       (limit = 50) => get<{ deployments: DeployLog[] }>(`/api/admin/deploys?limit=${limit}`),
  systemHealth:  () => get<SystemHealth>('/api/system-monitoring/health/deep'),
  dataSources:   () => get<any>('/api/system-monitoring/data-sources'),
  eodhdUsage:    () => get<any>('/api/system-monitoring/eodhd-usage'),
  apiStatus:     () => get<unknown>('/api/status'),
  apiControls:   () => get<unknown>('/api/config/api-controls'),
  configAction:  (url: string, opts: RequestInit) => {
    // Escape hatch for Admin page's generic configAction — pass path only
    return request<unknown>(opts.method || 'GET', url, opts.body ? JSON.parse(opts.body as string) : undefined);
  },
  submitBug:     (body: unknown) => post<{ success: boolean }>('/api/admin/bugs', body),
  logs: (params: { limit?: number; level?: string; hours?: number } = {}) => {
    const q = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
    return get<{ logs: TradingLog[] }>(`/system/logs?${q}`);
  },
  automationActivity: (limit = 200, uid?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (uid) q.set('uid', uid);
    return get<{ events: Array<Record<string, unknown>>; total: number }>(`/api/admin/automation/activity?${q}`);
  },
  auditTrail: (limit = 100) => get<unknown>(`/audit/trail?limit=${limit}`),
  errors: (params: { limit?: number; hours?: number } = {}) => {
    const q = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
    return get<{ errors: any[]; total: number }>(`/api/monitoring/errors?${q}`);
  },
  resetHistory: (targetUid?: string) =>
    post<any>('/api/admin/reset/history', { confirm: 'RESET', target_uid: targetUid }),
  purgeBots: (opts: { keepActive?: boolean; warRoomOnly?: boolean; targetUid?: string } = {}) =>
    post<any>('/api/admin/bots/purge', {
      confirm: 'PURGE',
      keep_active: opts.keepActive ?? true,
      war_room_only: opts.warRoomOnly ?? false,
      target_uid: opts.targetUid,
    }),
};

// ─── Account ──────────────────────────────────────────────────────────────────

export const accountApi = {
  view: () => get<unknown>('/accounts/view'),
};

// ─── Monitoring ───────────────────────────────────────────────────────────────

export const monitoringApi = {
  universeAudit:      () => get<any>('/api/titan/universe/audit'),
  tradingLimitsReset: () => post<unknown>('/api/trading-limits/reset'),
  fetch: (path: string) => get<unknown>(path),  // generic for dynamic monitoring paths
};

/** Closed-trade forensics: entry/exit logic, SL adherence, optional async AI insights */
export const tradeMonitoringApi = {
  transactions: (params: URLSearchParams) =>
    get<unknown>(`/api/titan/trade-monitoring/transactions?${params.toString()}`),
  stopLossReport: (params: URLSearchParams) =>
    get<unknown>(`/api/titan/trade-monitoring/stop-loss-report?${params.toString()}`),
  queueInsights: (exitLogIds: string[]) =>
    post<unknown>('/api/titan/trade-monitoring/insights/queue', { exit_log_ids: exitLogIds }),
};

// ─── Backtest ─────────────────────────────────────────────────────────────────

export const backtestApi = {
  startAuto:   (body: unknown) => post<{ run_id: string }>('/api/pattern-backtest/auto', body),
  status:      (runId: string) => get<{ status: string; progress?: number }>(`/api/pattern-backtest/status/${runId}`),
  results:     (runId: string) => get<unknown>(`/api/pattern-backtest/results/${runId}`),
  diagnose:    (patternId: string) => get<unknown>(`/api/pattern-backtest/diagnose/${patternId}`),
};

// ─── Bug Report (from BugReportWidget) ───────────────────────────────────────

export const bugReportApi = {
  submit: (body: unknown) => post<{ success: boolean }>('/api/admin/bugs', body),
};

// ─── Diagnostics ─────────────────────────────────────────────────────────────

// ─── YouTube Intelligence ─────────────────────────────────────────────────────

export interface YouTubeChannel {
  channel_id: string;
  name: string;
  handle: string;
  focus: string;
  enabled: boolean;
}

export interface YouTubeSettings {
  youtube_api_key: string;
  youtube_api_key_set: boolean;
  /** Supadata API key — primary fallback for videos without YouTube captions */
  supadata_api_key: string;
  supadata_api_key_set: boolean;
  /** OpenAI API key — last-resort fallback via yt-dlp audio download */
  whisper_api_key: string;
  whisper_api_key_set: boolean;
  enabled: boolean;
  max_videos_per_run: number;
  hours_lookback: number;
  enabled_channels: string[];
  min_confidence_score: number;
  stocks_focus_only: boolean;
  channels: YouTubeChannel[];
}

export interface VideoInsightRecord {
  video_id: string;
  channel_name: string;
  channel_focus: string;
  title: string;
  published_at: string;
  duration_minutes: number;
  key_insights: string[];
  mentioned_symbols: string[];
  market_sentiment: string;
  regime_signals: string[];
  risk_events_mentioned: string[];
  confidence_score: number;
  raw_summary: string;
}

export interface YouTubeInsightsResponse {
  status: string;
  date: string;
  insights: VideoInsightRecord[];
  total: number;
  channels_fetched: string[];
  message?: string;
}

export const youtubeApi = {
  getSettings: () => get<{ status: string; settings: YouTubeSettings }>('/api/settings/youtube'),
  saveSettings: (s: Partial<YouTubeSettings>) =>
    post<{ status: string; settings: YouTubeSettings }>('/api/settings/youtube', s),
  getInsights: (params?: { date?: string; limit?: number; min_confidence?: number }) => {
    const qs = params
      ? '?' +
        Object.entries(params)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${k}=${v}`)
          .join('&')
      : '';
    return get<YouTubeInsightsResponse>(`/api/youtube/insights${qs}`);
  },
  refreshInsights: () =>
    post<{
      status: string;
      message: string;
      insights_count?: number;
      channels_fetched?: string[];
      insights?: VideoInsightRecord[];
    }>('/api/youtube/insights/refresh'),
  diagnose: () =>
    get<{
      status: string;
      summary: string;
      results: Record<string, { ok: boolean; error?: string; hint?: string; [key: string]: unknown }>;
    }>('/api/youtube/diagnose'),
};

// ─── Earnings Intelligence ────────────────────────────────────────────────────

export interface EarningsSettings {
  enabled: boolean;
  tickers: string[];
  filing_types: string[];
  lookback_days: number;
  max_filings_per_run: number;
}

export interface EarningsInsightRecord {
  ticker: string;
  company_name: string;
  filing_type: string;
  filing_date: string;
  headline: string;
  eps_result: { actual: number; estimated: number; beat: boolean } | null;
  revenue_result: { actual_bn: number; estimated_bn: number; beat: boolean } | null;
  forward_guidance: string | null;
  key_insights: string[];
  mentioned_symbols: string[];
  sentiment: string;
  trading_implication: string;
  risk_events_mentioned: string[];
  confidence_score: number;
  raw_summary: string;
}

export const earningsApi = {
  getSettings: () =>
    get<{ status: string; settings: EarningsSettings }>('/api/settings/earnings'),
  saveSettings: (s: Partial<EarningsSettings>) =>
    post<{ status: string; settings: EarningsSettings }>('/api/settings/earnings', s),
  getInsights: (date?: string) =>
    get<{ status: string; date: string; insights: EarningsInsightRecord[]; total: number }>(
      `/api/earnings/insights${date ? `?date=${date}` : ''}`
    ),
  refreshInsights: () =>
    post<{
      status: string;
      message: string;
      insights_count?: number;
      tickers_fetched?: string[];
      insights?: EarningsInsightRecord[];
    }>('/api/earnings/insights/refresh'),
  diagnose: () =>
    get<{
      status: string;
      summary: string;
      tracked_tickers: string[];
      filing_types: string[];
      results: Record<string, { ok: boolean; error?: string; hint?: string; [key: string]: unknown }>;
    }>('/api/earnings/diagnose'),
};

// ─── Hermes Agent ──────────────────────────────────────────────────────────────

export const hermesApi = {
  context:         () => get<any>('/api/hermes/context'),
  patterns:        (days = 30) => get<any>(`/api/hermes/patterns?days=${days}`),
  decisionSummary: (patternId?: string, days = 30) =>
    get<any>(`/api/hermes/decision-summary?days=${days}${patternId ? `&pattern_id=${patternId}` : ''}`),
  optimize:        (body: { pattern_id: string; days?: number; strategy?: string }) =>
    post<any>('/api/hermes/optimize', body),
  optimizeStatus:  (jobId: string) => get<any>(`/api/hermes/optimize/${jobId}`),
  proposals:       (status?: string) =>
    get<any>(`/api/hermes/proposals${status ? `?status=${status}` : ''}`),
  propose:         (body: { pattern: any; rationale: string; regime_context?: string; sandbox_only?: boolean }) =>
    post<any>('/api/hermes/pattern/propose', body),
  approveProposal: (proposalId: string) =>
    post<any>(`/api/hermes/proposals/${proposalId}/approve`),
  improvements:    () => get<any>('/api/hermes/improvements'),
  metricHistory:   (patternId?: string, limit = 200) =>
    get<any>(`/api/hermes/metric-history?limit=${limit}${patternId ? `&pattern_id=${patternId}` : ''}`),
  // AI brief uses the existing intelligence chat endpoint.
  chat:            (message: string) =>
    post<{ reply: string; data?: any }>('/api/intelligence/chat', { message, conversation: [] }),
};

// ─── Ask Titan (Intelligence Query Agent) ────────────────────────────────────
// Always-available natural-language agent over the live trading platform. The
// backend tool-calls into telemetry/reporting and returns the tools it used.

export type AgentChatTurn = { role: 'user' | 'assistant'; content: string };

/** Handlers for the streaming agent. `onDelta`/`onDone`/`onError` are required; `onTool` is optional. */
export interface AgentStreamHandlers {
  onTool?: (name: string) => void;
  onDelta: (text: string) => void;
  onDone: (toolsUsed: string[]) => void;
  onError: (message: string) => void;
}

export const agentApi = {
  ask: (
    message: string,
    history?: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) =>
    post<{ reply: string; tools_used: string[] }>('/api/intelligence/agent', { message, history }),

  /**
   * Proactive "Latest insight" for the Ask Titan panel. `insight` is "" when the
   * AI is unavailable; the store maps that to no card.
   */
  insights: () =>
    get<{ insight: string; signals: Record<string, unknown>; generated_at: string }>(
      '/api/intelligence/insights',
    ),

  /**
   * SSE token-streaming variant of `ask`. Uses raw `fetch` (not the `request`
   * wrapper) because we need the streaming response body. Attaches the SAME
   * `Authorization: Bearer ${token}` header the rest of the client uses (token
   * from localStorage 'titan_token' — mirrors getToken()/authHeaders()).
   *
   * Parses the `text/event-stream` body: chunks are decoded, buffered, split on
   * the SSE record delimiter (`\n\n`); each record's `data:` lines are stripped
   * and JSON-parsed, then dispatched to handlers by `type`. Any non-OK HTTP,
   * network failure, or parse error is surfaced via `onError` so the caller can
   * fall back to the non-stream endpoint.
   */
  askStream: async (
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }> | undefined,
    handlers: AgentStreamHandlers,
  ): Promise<void> => {
    // Replicate authHeaders() exactly: token from localStorage, Bearer scheme.
    const token = localStorage.getItem('titan_token');
    if (!token) {
      handlers.onError('Not authenticated');
      return;
    }

    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/intelligence/agent/stream`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, history }),
      });
    } catch (err) {
      handlers.onError(err instanceof Error ? err.message : String(err));
      return;
    }

    if (!res.ok || !res.body) {
      let detail = `HTTP ${res.status}`;
      try {
        const t = await res.text();
        if (t) detail = t.length > 200 ? `${t.slice(0, 200)}…` : t;
      } catch {
        /* ignore */
      }
      handlers.onError(detail);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Dispatch a single parsed SSE event object to the right handler.
    const dispatch = (event: { type?: string; [k: string]: unknown }) => {
      switch (event.type) {
        case 'tool':
          if (typeof event.name === 'string') handlers.onTool?.(event.name);
          break;
        case 'delta':
          if (typeof event.text === 'string') handlers.onDelta(event.text);
          break;
        case 'done':
          handlers.onDone(Array.isArray(event.tools_used) ? (event.tools_used as string[]) : []);
          break;
        case 'error':
          handlers.onError(typeof event.error === 'string' ? event.error : 'Stream error');
          break;
        default:
          /* ignore unknown event types */
          break;
      }
    };

    // Parse the `data:` payload(s) within one SSE record (records split on \n\n).
    const handleRecord = (record: string) => {
      const dataLines = record
        .split('\n')
        .map((line) => line.replace(/\r$/, ''))
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice('data:'.length).trim());
      if (dataLines.length === 0) return;
      const payload = dataLines.join('\n');
      if (!payload) return;
      try {
        dispatch(JSON.parse(payload));
      } catch {
        /* skip un-parseable record; stream continues */
      }
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          let sep: number;
          // Split on the SSE record delimiter; process complete records.
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const record = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            handleRecord(record);
          }
        }
        if (done) break;
      }
      // Flush any trailing buffered record (stream may end without final \n\n).
      buffer += decoder.decode();
      if (buffer.trim()) handleRecord(buffer);
    } catch (err) {
      handlers.onError(err instanceof Error ? err.message : String(err));
    }
  },
};

export const diagnosticsApi = {
  entryBlockers:     (days = 7) => get<any>(`/api/titan/diagnostics/entry-blockers?days=${days}`),
  botDecisions:      (hours = 24) => get<any>(`/api/titan/diagnostics/bot-decisions?hours=${hours}`),
  aiTradingIssues:   (hours = 24) => get<any>(`/api/titan/diagnostics/ai-trading-issues?hours=${hours}`),
  scanTrace:         (minutes = 60) => get<any>(`/api/titan/diagnostics/scan-trace?minutes=${minutes}`),
  triggerBlockers:   (minutes = 60) => get<any>(`/api/titan/diagnostics/trigger-blockers?minutes=${minutes}`),
  evalSampleRate:    () => get<any>('/api/titan/diagnostics/eval-sample-rate'),
  setEvalSampleRate: (rate: number) => post<any>('/api/titan/diagnostics/eval-sample-rate', { rate }),
  evaluations:  (params: string) => get<any>(`/api/titan/decision-logs/evaluations?${params}`),
  logs:         (params: string) => get<any>(`/api/titan/decision-logs?${params}`),
  recentTrades: (days = 1, limit = 100) => get<any>(`/api/titan/decision-logs/trades?days=${days}&limit=${limit}`),
};
